"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import { ChevronRight, FolderOpen } from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";
import type { ReactNode } from "react";
import { ProjectStatusBadge } from "@/components/projects/project-status-badge";
import { ButtonLink } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Section } from "@/components/ui/section";
import { Skeleton, TextLineSkeleton } from "@/components/ui/skeleton";
import { metaTextClassName } from "@/components/ui/typography";
import { dateTimeFormat, relativeTimeFormat } from "@/lib/date-format";
import { cn } from "@/lib/utils";
function RecentActivitySection(t0) {
  const $ = _c(3);
  const {
    actions,
    children
  } = t0;
  let t1;
  if ($[0] !== actions || $[1] !== children) {
    t1 = <Section actions={actions} title="Atividade recente">{children}</Section>;
    $[0] = actions;
    $[1] = children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
const RECENT_PROJECTS_LIMIT = 8;

/**
 * Most recently updated projects for the organization dashboard. Loading shows a full page
 * of rows with the loaded row geometry (code + status badge line, producer line, time).
 * `pending` renders that loading state without querying, before Convex auth is ready.
 */
export function RecentActivity(t0) {
  const $ = _c(22);
  const {
    subscriptionLocked: t1,
    pending: t2
  } = t0;
  const subscriptionLocked = t1 === undefined ? false : t1;
  const pending = t2 === undefined ? false : t2;
  const {
    organizationSlug
  } = useParams();
  let t3;
  if ($[0] !== pending || $[1] !== subscriptionLocked) {
    t3 = subscriptionLocked || pending ? "skip" : {
      limit: RECENT_PROJECTS_LIMIT
    };
    $[0] = pending;
    $[1] = subscriptionLocked;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const recentProjects = useQuery(api.projects.queries.getRecentProjectsForDashboard, t3);
  const isLoading = pending || !subscriptionLocked && recentProjects === undefined;
  if (!organizationSlug) {
    return null;
  }
  const t4 = `/${organizationSlug}/projects`;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronRight />;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4) {
    t6 = <ButtonLink size="sm" variant="outline" href={t4}>Ver todas{t5}</ButtonLink>;
    $[4] = t4;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const viewAll = t6;
  if (isLoading) {
    let t7;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ul aria-busy="true" className="flex flex-col divide-y">{Array.from({
          length: RECENT_PROJECTS_LIMIT
        }).map(_temp)}</ul>;
      $[6] = t7;
    } else {
      t7 = $[6];
    }
    let t8;
    if ($[7] !== t7 || $[8] !== viewAll) {
      t8 = <RecentActivitySection actions={viewAll}>{t7}</RecentActivitySection>;
      $[7] = t7;
      $[8] = viewAll;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    return t8;
  }
  if (!recentProjects?.length) {
    const t7 = `/${organizationSlug}/projects/new`;
    let t8;
    if ($[10] !== t7) {
      t8 = <RecentActivitySection><EmptyState action={<ButtonLink variant="outline" href={t7}>Novo projeto</ButtonLink>} icon={FolderOpen} size="sm" title="Nenhuma atividade recente encontrada" /></RecentActivitySection>;
      $[10] = t7;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    return t8;
  }
  let t7;
  if ($[12] !== organizationSlug || $[13] !== recentProjects) {
    let t8;
    if ($[15] !== organizationSlug) {
      t8 = project => {
        const updatedAt = new Date(project.updatedAt);
        return <li className="flex items-start gap-3 py-2.5" key={project._id}><FolderOpen className="text-muted-foreground mt-0.5 size-4 shrink-0" /><div className="flex min-w-0 flex-1 items-start justify-between gap-3"><div className="min-w-0"><div className="flex min-w-0 flex-wrap items-center gap-2"><Link className="text-foreground hover:text-primary truncate font-semibold" href={`/${organizationSlug}/projects/${project._id}`}>{project.projectCode}</Link><ProjectStatusBadge status={project.status} /></div>{project.producerName || project.harvest ? <p className="text-muted-foreground truncate">{[project.producerName, project.harvest].filter(Boolean).join(" \xB7 ")}</p> : null}</div><time className={cn(metaTextClassName, "shrink-0 whitespace-nowrap tabular-nums")} dateTime={updatedAt.toISOString()} title={dateTimeFormat(updatedAt)}>{relativeTimeFormat(updatedAt)}</time></div></li>;
      };
      $[15] = organizationSlug;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t7 = recentProjects.map(t8);
    $[12] = organizationSlug;
    $[13] = recentProjects;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <ul className="flex flex-col divide-y">{t7}</ul>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8 || $[20] !== viewAll) {
    t9 = <RecentActivitySection actions={viewAll}>{t8}</RecentActivitySection>;
    $[19] = t8;
    $[20] = viewAll;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp(_, index) {
  return <li className="flex items-start gap-3 py-2.5" key={index}><FolderOpen className="text-muted-foreground mt-0.5 size-4 shrink-0" /><div className="flex min-w-0 flex-1 items-start justify-between gap-3"><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><TextLineSkeleton className="w-16" /><Skeleton className="h-5 w-14 rounded-full" /></div><TextLineSkeleton className="w-40 max-w-full" /></div><TextLineSkeleton className="w-12 shrink-0" /></div></li>;
}